import { useEffect, useRef, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';

/**
 * Custom hook to monitor connection state
 * LiveKit handles ALL reconnection automatically - we just monitor and report state
 */
export const useReconnection = (onReconnect, maxRetries = 20) => {
  const roomContext = useRoomContext();
  const room = roomContext?.room;
  const [connectionState, setConnectionState] = useState(null);
  const reconnectAttemptsRef = useRef(0);
  const previousStateRef = useRef(null);
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    if (!room) return;

    const handleConnectionStateChange = (state) => {
      // Only log state transitions, not repeated states
      if (previousStateRef.current !== state) {
        console.log('Connection state changed:', previousStateRef.current, '->', state);
        previousStateRef.current = state;
      }

      setConnectionState((prev) => (prev === state ? prev : state));

      if (state === 'connected') {
        reconnectAttemptsRef.current = 0;
        onReconnectRef.current?.(true);
      } else if (state === 'reconnecting') {
        // LiveKit is automatically reconnecting - this is good, don't interfere
        reconnectAttemptsRef.current++;
        console.log(`LiveKit reconnecting (attempt ${reconnectAttemptsRef.current})`);

        // Only call onReconnect(false) if max retries exceeded
        // But LiveKit's reconnectPolicy handles this, so we just monitor
        if (reconnectAttemptsRef.current >= maxRetries) {
          console.warn('Max reconnection attempts reached - LiveKit will stop trying');
          // Don't call onReconnect here - let LiveKit handle it
        }
      } else if (state === 'disconnected') {
        // Disconnected - LiveKit will try to reconnect automatically
        // Don't do anything here - let LiveKit handle it
        console.log('Disconnected - LiveKit will attempt automatic reconnection');
      }
    };

    const handleDisconnected = (reason) => {
      console.log('Room disconnected event:', reason);
      // LiveKit will handle reconnection automatically based on reconnectPolicy
      // We don't need to do anything here
    };

    // Listen to connection state changes
    room.on('connectionStateChanged', handleConnectionStateChange);
    room.on('disconnected', handleDisconnected);

    // Set initial state
    if (room.state) {
      setConnectionState((prev) => (prev === room.state ? prev : room.state));
      previousStateRef.current = room.state;
    }

    // Cleanup
    return () => {
      room.off('connectionStateChanged', handleConnectionStateChange);
      room.off('disconnected', handleDisconnected);
    };
  }, [room, maxRetries]);

  return { connectionState, reconnectAttempts: reconnectAttemptsRef.current };
};

